import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import MobileSupervisionsHome from "@/components/mobile/MobileSupervisionsHome";
import {
  ClipboardCheck, Plus, ChevronDown, ChevronRight, Trash2, ShieldCheck, Lock, Unlock,
  CalendarClock, AlertTriangle, CheckCircle2, ListChecks, Grid3x3, BookOpen, Settings2,
  Pencil, FileSignature,
} from "lucide-react";

const TABS = [
  { key: "overview", label: "Overview", Icon: BookOpen },
  { key: "records", label: "Records", Icon: ClipboardCheck },
  { key: "matrix", label: "Matrix", Icon: Grid3x3 },
  { key: "actions", label: "Actions", Icon: ListChecks },
];

const STATUS_TONE = {
  draft: "bg-slate-100 text-slate-700 border-slate-300",
  scheduled: "bg-sky-50 text-sky-800 border-sky-300",
  in_progress: "bg-amber-50 text-amber-800 border-amber-300",
  completed: "bg-emerald-50 text-emerald-800 border-emerald-300",
  cancelled: "bg-red-50 text-red-700 border-red-300",
};

const DUE_TONE = {
  overdue: "bg-red-100 text-red-800 border-red-300",
  due_soon: "bg-amber-100 text-amber-900 border-amber-400",
  on_track: "bg-emerald-50 text-emerald-800 border-emerald-300",
  unknown: "bg-slate-50 text-slate-500 border-slate-200",
};

const DEFAULT_AGENDA = [
  "Welfare & wellbeing",
  "Workload & rota",
  "Client care & safety",
  "Medication competency",
  "Policies & procedures",
  "Infection prevention & control",
  "Training & DBS",
  "Concerns / complaints raised",
  "Previous action review",
  "Personal development",
];

const SECTIONS = [
  { key: "performance",              label: "Performance & Practice",           questions: ["Punctuality and attendance", "Care records accurately completed", "Follows care plans and risk assessments", "Communicates effectively with team and family"] },
  { key: "client_care_safety",       label: "Client Care & Safety",             questions: ["Understands and follows current care plans", "Recognises deterioration and escalates appropriately", "Consistent dignity, choice and person-centred approach", "Safeguarding awareness up to date"] },
  { key: "policies_infection",       label: "Policies & Infection Prevention",  questions: ["Follows PPE and hand hygiene protocols", "Confident with current policies (safeguarding, medication, IPC)", "Reports incidents and near-misses promptly"] },
  { key: "induction",                label: "Induction (where applicable)",     questions: ["Care Certificate progress reviewed", "Shadowing & sign-off complete for role", "Probation objectives on track"] },
  { key: "training_dbs_development", label: "Training / DBS / Development",     questions: ["Mandatory training in date", "DBS in date", "Development goals reviewed and updated"] },
  { key: "wellbeing_workload",       label: "Wellbeing & Workload",             questions: ["Manageable workload and hours", "Physical + mental wellbeing supported", "Reasonable adjustments in place where needed"] },
  { key: "right_support_care_culture", label: "Right Support, Right Care, Right Culture", questions: ["Choice, control and inclusion promoted for people supported", "Care is person-centred, not service-led", "Culture supports learning from things that go wrong"] },
  { key: "complaints_open_culture",  label: "Complaints / Concerns / Open Culture", questions: ["Any complaints/concerns raised or observed?", "Comfortable using the whistleblowing/speak-up route?", "Any feedback for the organisation?"] },
];

const responseColor = (r) => ({
  yes: "bg-emerald-100 text-emerald-800 border-emerald-300",
  no: "bg-red-100 text-red-800 border-red-300",
  partial: "bg-amber-100 text-amber-800 border-amber-300",
  na: "bg-slate-100 text-slate-600 border-slate-300",
}[r] || "bg-white border-slate-300 text-slate-600");

/* ---------- Sub-components ---------- */

function SectionCard({ title, children, open, onToggle, testid, badge }) {
  return (
    <div className="border border-slate-200 rounded-md bg-white" data-testid={testid}>
      <button type="button" onClick={onToggle} className="w-full flex items-center gap-2 px-3 py-2 hover:bg-slate-50 text-left">
        {open ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
        <div className="text-sm font-semibold text-slate-800 flex-1">{title}</div>
        {badge}
      </button>
      {open && <div className="border-t border-slate-100 p-3 space-y-3">{children}</div>}
    </div>
  );
}

function QuestionRow({ q, index, value, onChange, testid }) {
  const responses = ["yes", "no", "partial", "na"];
  return (
    <div className="border border-slate-100 rounded p-2 bg-slate-50/40" data-testid={testid}>
      <div className="text-[13px] text-slate-800 mb-1.5"><span className="text-slate-400 mr-1">{index + 1}.</span>{q}</div>
      <div className="flex flex-wrap items-center gap-1 mb-1.5">
        {responses.map((r) => (
          <button
            key={r}
            type="button"
            onClick={() => onChange({ ...value, response: r })}
            data-testid={`${testid}-${r}`}
            className={`min-h-[44px] flex-1 md:flex-none text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 border rounded ${value?.response === r ? responseColor(r) : "bg-white text-slate-400 border-slate-200 hover:text-slate-700"}`}
          >{r.toUpperCase()}</button>
        ))}
        <label className="w-full md:w-auto md:ml-auto flex items-center gap-1 text-[11px] text-slate-600 cursor-pointer min-h-[44px]" data-testid={`${testid}-action-toggle`}>
          <Checkbox checked={!!value?.action_required} onCheckedChange={(v) => onChange({ ...value, action_required: !!v })} />
          Action required
        </label>
      </div>
      <Textarea rows={1} value={value?.comment || ""} onChange={(e) => onChange({ ...value, comment: e.target.value })} placeholder="Comments / evidence…" className="text-xs" data-testid={`${testid}-comment`} />
    </div>
  );
}

function ActionRow({ a, onChange, onRemove, staffOptions }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-start py-1.5 border-b border-slate-100" data-testid={`action-row-${a.id}`}>
      <Input className="md:col-span-4 h-11 md:h-8 text-xs" value={a.action || ""} onChange={(e) => onChange({ ...a, action: e.target.value })} placeholder="Action" data-testid={`action-text-${a.id}`} />
      <Select value={a.responsible_person_user_id || "__none"} onValueChange={(v) => {
        const opt = staffOptions.find((s) => s.id === v);
        onChange({ ...a, responsible_person_user_id: v === "__none" ? null : v, responsible_person_name: opt?.name || null });
      }}>
        <SelectTrigger className="md:col-span-3 h-11 md:h-8 text-xs"><SelectValue placeholder="Owner" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="__none">Unassigned</SelectItem>
          {staffOptions.map((s) => <SelectItem key={s.id} value={s.id}>{s.name || s.email}</SelectItem>)}
        </SelectContent>
      </Select>
      <Select value={a.priority || "medium"} onValueChange={(v) => onChange({ ...a, priority: v })}>
        <SelectTrigger className="md:col-span-1 h-11 md:h-8 text-xs"><SelectValue /></SelectTrigger>
        <SelectContent><SelectItem value="low">Low</SelectItem><SelectItem value="medium">Med</SelectItem><SelectItem value="high">High</SelectItem></SelectContent>
      </Select>
      <Input type="date" className="md:col-span-2 h-11 md:h-8 text-xs" value={a.due_date || ""} onChange={(e) => onChange({ ...a, due_date: e.target.value })} />
      <Select value={a.status || "open"} onValueChange={(v) => onChange({ ...a, status: v })}>
        <SelectTrigger className="md:col-span-1 h-11 md:h-8 text-xs"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="open">Open</SelectItem>
          <SelectItem value="in_progress">In progress</SelectItem>
          <SelectItem value="completed">Completed</SelectItem>
          <SelectItem value="no_longer_required">No longer required</SelectItem>
        </SelectContent>
      </Select>
      <button type="button" onClick={onRemove} className="text-red-500 hover:text-red-700 text-xs md:col-span-1 min-h-[44px] md:min-h-0 flex items-center justify-center" data-testid={`action-remove-${a.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
    </div>
  );
}

function StatusPill({ status }) {
  return <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold border px-1.5 py-0.5 rounded ${STATUS_TONE[status] || STATUS_TONE.draft}`}>{(status || "draft").replace("_", " ")}</span>;
}

/* ---------- Supervision Form Dialog ---------- */

function SupervisionForm({ open, onOpenChange, initial, staffOptions, currentUser, onSaved }) {
  const [form, setForm] = useState(null);
  const [sectionOpen, setSectionOpen] = useState({});
  const [saving, setSaving] = useState(false);
  const [signPasswordOpen, setSignPasswordOpen] = useState(null); // "staff" | "supervisor"
  const [password, setPassword] = useState("");
  const [nameTyped, setNameTyped] = useState("");
  const [outstanding, setOutstanding] = useState([]);
  const [prev, setPrev] = useState(null);

  useEffect(() => {
    if (!open) return;
    const base = {
      staff_user_id: "",
      staff_name: "",
      role: "",
      site_id: "",
      supervisor_user_id: currentUser?.id || "",
      supervisor_name: currentUser?.name || currentUser?.email || "",
      supervision_type: "supervision",
      scheduled_date: new Date().toISOString().slice(0, 10),
      held_date: "",
      status: "scheduled",
      agenda: DEFAULT_AGENDA.map((topic) => ({ topic, checked: false })),
      previous_actions_review: [],
      performance: {}, client_care_safety: {}, policies_infection: {},
      induction: { applicable: false }, training_dbs_development: {}, wellbeing_workload: {},
      right_support_care_culture: {}, complaints_open_culture: {},
      supervisor_feedback: "",
      action_plan: [],
    };
    setForm(initial ? { ...base, ...initial } : base);
    setSectionOpen({ details: true, agenda: true });
    setPassword(""); setNameTyped(""); setSignPasswordOpen(null);
  }, [open, initial, currentUser]);

  // When staff is selected → load previous supervision + outstanding actions
  useEffect(() => {
    if (!form?.staff_user_id) { setOutstanding([]); setPrev(null); return; }
    api.get(`/supervisions/staff/${form.staff_user_id}/outstanding-actions`).then((r) => setOutstanding(r.data || [])).catch(() => setOutstanding([]));
    api.get(`/supervisions/staff/${form.staff_user_id}/previous`).then((r) => setPrev(r.data)).catch(() => setPrev(null));
  }, [form?.staff_user_id]);

  if (!open || !form) return null;
  const readOnly = !!initial?.read_only;

  const patchQ = (sectionKey, i, next) => {
    setForm((f) => {
      const sec = { ...(f[sectionKey] || {}) };
      const qs = [...(sec.questions || [])];
      qs[i] = next;
      sec.questions = qs;
      return { ...f, [sectionKey]: sec };
    });
  };

  const save = async () => {
    if (!form.staff_user_id) { toast.error("Pick a staff member"); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      const staff = staffOptions.find((s) => s.id === form.staff_user_id);
      if (staff) {
        payload.staff_name = staff.name || staff.email;
        payload.role = payload.role || staff.role;
        payload.site_id = payload.site_id || staff.site_id;
      }
      const r = initial?.id
        ? await api.put(`/supervisions/${initial.id}`, payload)
        : await api.post("/supervisions", payload);
      toast.success(initial?.id ? "Supervision updated" : "Supervision created");
      onSaved(r.data);
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const submitSignoff = async () => {
    if (!password) { toast.error("Enter your password to confirm sign-off"); return; }
    try {
      const r = await api.post(`/supervisions/${initial.id}/signoff`, { role: signPasswordOpen, password, name_typed: nameTyped });
      toast.success(`Signed off as ${signPasswordOpen}`);
      setSignPasswordOpen(null); setPassword(""); setNameTyped("");
      onSaved(r.data);
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const canSignAsStaff = initial?.id && !initial?.read_only && form.staff_user_id === currentUser?.id && !initial?.staff_signoff;
  const canSignAsSupervisor = initial?.id && !initial?.read_only && form.supervisor_user_id === currentUser?.id && !initial?.supervisor_signoff;

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-hidden flex flex-col p-0" data-testid="supervision-dialog">
        <DialogHeader className="p-4 border-b border-slate-200">
          <DialogTitle className="flex items-center gap-2">
            <ClipboardCheck className="w-4 h-4 text-cyan-700" />
            {initial?.id ? "Supervision — " + (initial.staff_name || "") : "New supervision"}
            {readOnly && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold text-slate-600 bg-slate-100 border border-slate-300 px-1.5 py-0.5 rounded ml-2"><Lock className="w-3 h-3" /> Read only</span>}
          </DialogTitle>
          <DialogDescription>Structured supervision workflow — Yes/No/Partial/N/A with evidence + action plan.</DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {/* Supervision Details */}
          <SectionCard title="Supervision Details" open={sectionOpen.details} onToggle={() => setSectionOpen((s) => ({ ...s, details: !s.details }))} testid="section-details">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><Label className="text-xs">Staff member *</Label>
                <Select value={form.staff_user_id} onValueChange={(v) => setForm({ ...form, staff_user_id: v })} disabled={readOnly}>
                  <SelectTrigger data-testid="sup-staff"><SelectValue placeholder="Pick staff" /></SelectTrigger>
                  <SelectContent>{staffOptions.map((s) => <SelectItem key={s.id} value={s.id}>{s.name || s.email} <span className="text-[10px] text-slate-400 ml-1">{s.role}</span></SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Type</Label>
                <Select value={form.supervision_type} onValueChange={(v) => setForm({ ...form, supervision_type: v })} disabled={readOnly}>
                  <SelectTrigger data-testid="sup-type"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="supervision">Supervision</SelectItem><SelectItem value="appraisal">Annual Appraisal</SelectItem><SelectItem value="1-1">1-to-1</SelectItem><SelectItem value="induction">Induction</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Scheduled date</Label><Input type="date" value={form.scheduled_date || ""} onChange={(e) => setForm({ ...form, scheduled_date: e.target.value })} disabled={readOnly} data-testid="sup-scheduled" /></div>
              <div><Label className="text-xs">Held date</Label><Input type="date" value={form.held_date || ""} onChange={(e) => setForm({ ...form, held_date: e.target.value })} disabled={readOnly} data-testid="sup-held" /></div>
              <div><Label className="text-xs">Supervisor</Label>
                <Select value={form.supervisor_user_id} onValueChange={(v) => { const s = staffOptions.find((x) => x.id === v); setForm({ ...form, supervisor_user_id: v, supervisor_name: s?.name || s?.email }); }} disabled={readOnly}>
                  <SelectTrigger data-testid="sup-supervisor"><SelectValue placeholder="Pick supervisor" /></SelectTrigger>
                  <SelectContent>{staffOptions.filter((s) => ["admin", "manager"].includes(s.role)).map((s) => <SelectItem key={s.id} value={s.id}>{s.name || s.email}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })} disabled={readOnly}>
                  <SelectTrigger data-testid="sup-status"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="draft">Draft</SelectItem><SelectItem value="scheduled">Scheduled</SelectItem><SelectItem value="in_progress">In progress</SelectItem><SelectItem value="completed">Completed</SelectItem><SelectItem value="cancelled">Cancelled</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
          </SectionCard>

          {/* Agenda */}
          <SectionCard title="Agenda / Topics Discussed" open={sectionOpen.agenda} onToggle={() => setSectionOpen((s) => ({ ...s, agenda: !s.agenda }))} testid="section-agenda">
            <div className="grid grid-cols-2 gap-x-4 gap-y-1">
              {(form.agenda || []).map((t, i) => (
                <label key={i} className="flex items-center gap-2 text-[13px] cursor-pointer" data-testid={`agenda-${i}`}>
                  <Checkbox checked={!!t.checked} disabled={readOnly} onCheckedChange={(v) => { const list = [...form.agenda]; list[i] = { ...t, checked: !!v }; setForm({ ...form, agenda: list }); }} />
                  {t.topic}
                </label>
              ))}
            </div>
          </SectionCard>

          {/* Previous supervision review */}
          <SectionCard title={`Previous Supervision Review (${outstanding.length} outstanding action${outstanding.length === 1 ? "" : "s"})`} open={sectionOpen.previous} onToggle={() => setSectionOpen((s) => ({ ...s, previous: !s.previous }))} testid="section-previous">
            {outstanding.length === 0 ? <div className="text-xs text-slate-500 italic">No outstanding actions from previous supervisions.</div> : (
              <div className="space-y-1.5">
                {outstanding.map((a) => (
                  <div key={a.id} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center border border-slate-100 rounded p-2 bg-slate-50/40" data-testid={`prev-action-${a.id}`}>
                    <div className="md:col-span-6 text-[13px] text-slate-700">{a.action}<div className="text-[10px] text-slate-500">due {a.due_date || "—"} · {a.priority}</div></div>
                    <Select value={(form.previous_actions_review.find((r) => r.action_id === a.id) || {}).decision || "outstanding"} onValueChange={(v) => {
                      const list = form.previous_actions_review.filter((r) => r.action_id !== a.id);
                      list.push({ action_id: a.id, decision: v });
                      setForm({ ...form, previous_actions_review: list });
                    }} disabled={readOnly}>
                      <SelectTrigger className="md:col-span-3 h-11 md:h-8 text-xs" data-testid={`prev-action-decision-${a.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="completed">Completed</SelectItem>
                        <SelectItem value="outstanding">Outstanding</SelectItem>
                        <SelectItem value="carried_forward">Carry forward</SelectItem>
                        <SelectItem value="no_longer_required">No longer required</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input className="md:col-span-3 h-11 md:h-8 text-xs" placeholder="Note" value={(form.previous_actions_review.find((r) => r.action_id === a.id) || {}).note || ""} onChange={(e) => {
                      const list = form.previous_actions_review.filter((r) => r.action_id !== a.id);
                      list.push({ action_id: a.id, decision: (form.previous_actions_review.find((r) => r.action_id === a.id) || {}).decision || "outstanding", note: e.target.value });
                      setForm({ ...form, previous_actions_review: list });
                    }} disabled={readOnly} />
                  </div>
                ))}
              </div>
            )}
          </SectionCard>

          {/* Structured question sections */}
          {SECTIONS.map((sec) => {
            const opt = sec.key === "induction" ? { applicable: form.induction?.applicable, hidden: !form.induction?.applicable } : null;
            const qs = (form[sec.key]?.questions?.length ? form[sec.key].questions : sec.questions.map((q) => ({ q, response: null, comment: "", action_required: false })));
            const short = qs.filter((q) => q.action_required).length;
            return (
              <SectionCard key={sec.key} title={sec.label} open={sectionOpen[sec.key]} onToggle={() => setSectionOpen((s) => ({ ...s, [sec.key]: !s[sec.key] }))} testid={`section-${sec.key}`} badge={short > 0 && <span className="text-[10px] uppercase tracking-widest font-semibold text-amber-800 bg-amber-100 border border-amber-300 px-1.5 py-0.5 rounded">{short} action</span>}>
                {sec.key === "induction" && (
                  <label className="flex items-center gap-2 text-xs cursor-pointer">
                    <Checkbox checked={!!form.induction?.applicable} disabled={readOnly} onCheckedChange={(v) => setForm({ ...form, induction: { ...(form.induction || {}), applicable: !!v } })} />
                    This member is currently in induction / probation
                  </label>
                )}
                {(sec.key !== "induction" || form.induction?.applicable) && qs.map((q, i) => (
                  <QuestionRow key={i} q={q.q || sec.questions[i]} index={i} value={q} onChange={(next) => patchQ(sec.key, i, { ...next, q: next.q || sec.questions[i] })} testid={`q-${sec.key}-${i}`} />
                ))}
              </SectionCard>
            );
          })}

          {/* Supervisor feedback */}
          <SectionCard title="Supervisor Feedback" open={sectionOpen.feedback} onToggle={() => setSectionOpen((s) => ({ ...s, feedback: !s.feedback }))} testid="section-feedback">
            <Textarea rows={4} value={form.supervisor_feedback || ""} onChange={(e) => setForm({ ...form, supervisor_feedback: e.target.value })} placeholder="Strengths, areas for development, positive recognition…" disabled={readOnly} data-testid="supervisor-feedback" />
          </SectionCard>

          {/* Action Plan */}
          <SectionCard title={`Action Plan (${form.action_plan.length})`} open={sectionOpen.actions} onToggle={() => setSectionOpen((s) => ({ ...s, actions: !s.actions }))} testid="section-actions">
            <div className="grid grid-cols-12 gap-2 text-[10px] uppercase tracking-widest text-slate-500 font-semibold px-1">
              <div className="col-span-4">Action</div><div className="col-span-3">Owner</div><div className="col-span-1">Pri</div><div className="col-span-2">Due</div><div className="col-span-1">Status</div><div className="col-span-1"></div>
            </div>
            {form.action_plan.map((a) => (
              <ActionRow key={a.id} a={a} staffOptions={staffOptions} onChange={(next) => setForm({ ...form, action_plan: form.action_plan.map((x) => x.id === a.id ? next : x) })} onRemove={() => setForm({ ...form, action_plan: form.action_plan.filter((x) => x.id !== a.id) })} />
            ))}
            {!readOnly && (
              <Button type="button" size="sm" variant="outline" className="h-8 mt-1" onClick={() => setForm({ ...form, action_plan: [...form.action_plan, { id: crypto.randomUUID(), action: "", priority: "medium", status: "open" }] })} data-testid="add-action">
                <Plus className="w-3.5 h-3.5 mr-1.5" /> Add action
              </Button>
            )}
          </SectionCard>

          {/* Sign-off */}
          <SectionCard title="Staff / Supervisor Sign-off" open={sectionOpen.signoff} onToggle={() => setSectionOpen((s) => ({ ...s, signoff: !s.signoff }))} testid="section-signoff">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="p-3 border border-slate-200 rounded bg-slate-50/40">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Staff</div>
                {initial?.staff_signoff ? (
                  <div className="text-xs" data-testid="staff-signoff-block"><CheckCircle2 className="w-3 h-3 inline text-emerald-600" /> {initial.staff_signoff.name} · {new Date(initial.staff_signoff.timestamp).toLocaleString()}</div>
                ) : canSignAsStaff ? (
                  <Button type="button" size="sm" variant="outline" onClick={() => setSignPasswordOpen("staff")} data-testid="btn-signoff-staff"><FileSignature className="w-3.5 h-3.5 mr-1.5" /> Sign as staff</Button>
                ) : <div className="text-xs text-slate-500 italic">Pending — must be signed by the supervised staff member.</div>}
              </div>
              <div className="p-3 border border-slate-200 rounded bg-slate-50/40">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Supervisor</div>
                {initial?.supervisor_signoff ? (
                  <div className="text-xs" data-testid="supervisor-signoff-block"><CheckCircle2 className="w-3 h-3 inline text-emerald-600" /> {initial.supervisor_signoff.name} · {new Date(initial.supervisor_signoff.timestamp).toLocaleString()}</div>
                ) : canSignAsSupervisor ? (
                  <Button type="button" size="sm" variant="outline" onClick={() => setSignPasswordOpen("supervisor")} data-testid="btn-signoff-supervisor"><FileSignature className="w-3.5 h-3.5 mr-1.5" /> Sign as supervisor</Button>
                ) : <div className="text-xs text-slate-500 italic">Pending — must be signed by the assigned supervisor.</div>}
              </div>
            </div>
            {readOnly && (
              <div className="text-[11px] text-slate-500 flex items-center gap-1 mt-1"><Lock className="w-3 h-3" /> Record is fully signed off. Authorised amendments are separately audit-logged.</div>
            )}
          </SectionCard>
        </div>

        <DialogFooter className="p-4 border-t border-slate-200">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          {!readOnly && <Button onClick={save} disabled={saving} className="bg-cyan-700 hover:bg-cyan-800" data-testid="btn-save-supervision">{saving ? "Saving…" : (initial?.id ? "Save changes" : "Create supervision")}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Sign-off password prompt */}
    <Dialog open={!!signPasswordOpen} onOpenChange={(v) => !v && setSignPasswordOpen(null)}>
      <DialogContent data-testid="signoff-password-dialog">
        <DialogHeader>
          <DialogTitle>Confirm sign-off</DialogTitle>
          <DialogDescription>You must re-enter your password. Typed initials alone are not accepted as an electronic signature.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Full name (optional, for the record)</Label><Input value={nameTyped} onChange={(e) => setNameTyped(e.target.value)} placeholder={currentUser?.name || ""} data-testid="signoff-name-typed" /></div>
          <div><Label className="text-xs">Password *</Label><Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="signoff-password" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setSignPasswordOpen(null)}>Cancel</Button>
          <Button onClick={submitSignoff} className="bg-cyan-700 hover:bg-cyan-800" data-testid="btn-confirm-signoff"><ShieldCheck className="w-3.5 h-3.5 mr-1.5" /> Sign & submit</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}

/* ---------- Main Page ---------- */

export default function Supervisions() {
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const [tab, setTab] = useState(params.get("tab") || "overview");
  const [records, setRecords] = useState([]);
  const [staff, setStaff] = useState([]);
  const [summary, setSummary] = useState(null);
  const [matrixData, setMatrixData] = useState(null);
  const [actions, setActions] = useState([]);
  const [settings, setSettings] = useState(null);
  const [settingsDraft, setSettingsDraft] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const canManage = user?.role === "admin" || user?.role === "manager";

  const load = async () => {
    try {
      const [r, u, s, m, a, os] = await Promise.all([
        api.get("/supervisions"),
        api.get("/auth/users"),
        api.get("/supervisions/summary"),
        api.get("/supervisions/matrix"),
        api.get("/supervisions/actions"),
        api.get("/org-settings"),
      ]);
      setRecords(r.data || []);
      setStaff(u.data || []);
      setSummary(s.data);
      setMatrixData(m.data);
      setActions(a.data || []);
      setSettings(os.data);
      setSettingsDraft(os.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const switchTab = (t) => { setTab(t); setParams({ tab: t }); };

  const openNew = () => { setEditing(null); setDialogOpen(true); };
  const openEdit = (rec) => { setEditing(rec); setDialogOpen(true); };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const r = await api.put("/org-settings", settingsDraft);
      setSettings(r.data); setSettingsDraft(r.data);
      toast.success("Organisation settings updated");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingSettings(false); }
  };

  const markAction = async (supId, actionId, next) => {
    try {
      await api.post(`/supervisions/${supId}/actions/${actionId}/mark`, next);
      toast.success("Action updated");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const orgName = settings?.organisation_name || "Ayobell Care";

  return (
    <div className="flex-1 overflow-y-auto bg-slate-50/40">
      {/* ================ MOBILE (<lg) ================ */}
      <div className="lg:hidden flex-1 min-h-0 flex flex-col" data-testid="supervisions-mobile-wrap">
        <MobileSupervisionsHome
          tab={tab} setTab={switchTab}
          records={records} matrixData={matrixData} actions={actions} summary={summary}
          settings={settings} settingsDraft={settingsDraft} setSettingsDraft={setSettingsDraft}
          saveSettings={saveSettings} savingSettings={savingSettings}
          canManage={canManage}
          onRefresh={load} onNew={openNew} onOpen={openEdit}
          onMark={markAction}
        />
      </div>

      {/* ================ DESKTOP (>=lg) — untouched ================ */}
      <div className="hidden lg:block" data-testid="supervisions-desktop-wrap">
      {/* Header */}
      <div className="border-b border-slate-200 bg-white px-6 py-4 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">People · {orgName}</div>
          <h1 className="font-heading text-2xl font-semibold text-slate-900" data-testid="supervisions-title">Supervision &amp; Appraisals</h1>
        </div>
        {canManage && <Button onClick={openNew} className="bg-cyan-700 hover:bg-cyan-800" data-testid="btn-new-supervision"><Plus className="w-4 h-4 mr-1.5" /> New supervision</Button>}
      </div>

      {/* Tabs */}
      <div className="px-6 border-b border-slate-200 bg-white flex items-center gap-1 overflow-x-auto">
        {TABS.map(({ key, label, Icon }) => (
          <button key={key} onClick={() => switchTab(key)} data-testid={`tab-${key}`} className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px flex items-center gap-1.5 ${tab === key ? "border-cyan-700 text-cyan-800" : "border-transparent text-slate-600 hover:text-slate-900"}`}>
            <Icon className="w-3.5 h-3.5" /> {label}
          </button>
        ))}
      </div>

      <div className="p-6 space-y-6">
        {/* Overview */}
        {tab === "overview" && summary && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="overview-kpi">
              <KpiCard label="Overdue" value={summary.overdue} Icon={AlertTriangle} tone="red" testid="kpi-overdue" />
              <KpiCard label="Due Soon" value={summary.due_soon} Icon={CalendarClock} tone="amber" testid="kpi-due-soon" />
              <KpiCard label="Never Supervised" value={summary.never_supervised} Icon={AlertTriangle} tone="slate" testid="kpi-never" />
              <KpiCard label="Open Actions" value={summary.open_actions} Icon={ListChecks} tone="sky" testid="kpi-actions" />
            </div>
            {/* Org settings inline */}
            <div className="bg-white border border-slate-200 rounded-md p-4" data-testid="overview-settings">
              <div className="flex items-center gap-2 mb-2">
                <Settings2 className="w-4 h-4 text-slate-600" />
                <div className="text-sm font-semibold">Organisation supervision policy</div>
                {!canManage && <span className="text-[10px] uppercase tracking-widest text-slate-400 ml-1">read-only</span>}
              </div>
              {settingsDraft && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <div><Label className="text-xs">Organisation name</Label><Input value={settingsDraft.organisation_name || ""} onChange={(e) => setSettingsDraft({ ...settingsDraft, organisation_name: e.target.value })} disabled={!canManage} data-testid="settings-org-name" /></div>
                  <div><Label className="text-xs">Supervisions per year</Label><Input type="number" min="0" value={settingsDraft.supervisions_per_year} onChange={(e) => setSettingsDraft({ ...settingsDraft, supervisions_per_year: Number(e.target.value) || 0 })} disabled={!canManage} data-testid="settings-per-year" /></div>
                  <div className="flex items-center gap-2 pt-6"><Checkbox checked={!!settingsDraft.annual_appraisal} onCheckedChange={(v) => setSettingsDraft({ ...settingsDraft, annual_appraisal: !!v })} disabled={!canManage} data-testid="settings-annual" /><Label className="text-xs">Annual appraisal in addition</Label></div>
                  <div><Label className="text-xs">Due-soon window (days)</Label><Input type="number" min="0" value={settingsDraft.due_soon_days} onChange={(e) => setSettingsDraft({ ...settingsDraft, due_soon_days: Number(e.target.value) || 0 })} disabled={!canManage} data-testid="settings-due-soon" /></div>
                  {canManage && <div className="md:col-span-4"><Button size="sm" onClick={saveSettings} disabled={savingSettings || JSON.stringify(settings) === JSON.stringify(settingsDraft)} data-testid="btn-save-settings" className="bg-cyan-700 hover:bg-cyan-800">{savingSettings ? "Saving…" : "Save policy"}</Button></div>}
                </div>
              )}
            </div>
          </>
        )}

        {/* Records */}
        {tab === "records" && (
          <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="records-panel">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-600">
                <tr><th className="px-3 py-2 text-left">Staff</th><th className="text-left">Type</th><th className="text-left">Scheduled</th><th className="text-left">Held</th><th className="text-left">Supervisor</th><th className="text-left">Status</th><th className="text-left">Next Due</th><th></th></tr>
              </thead>
              <tbody>
                {records.length === 0 && <tr><td colSpan={8} className="text-center text-slate-400 py-8 italic">No supervisions yet — click "New supervision" to add the first one.</td></tr>}
                {records.map((r) => (
                  <tr key={r.id} className="border-t border-slate-100" data-testid={`record-row-${r.id}`}>
                    <td className="px-3 py-2 font-medium">{r.staff_name || <span className="text-slate-400">—</span>}</td>
                    <td>{r.supervision_type || r.kind || "supervision"}</td>
                    <td>{r.scheduled_date || <span className="text-slate-400">—</span>}</td>
                    <td>{r.held_date || <span className="text-slate-400">—</span>}</td>
                    <td>{r.supervisor_name || r.conducted_by || <span className="text-slate-400">—</span>}</td>
                    <td><StatusPill status={r.status} /></td>
                    <td>{r.next_due_date || <span className="text-slate-400">—</span>}</td>
                    <td className="pr-3 text-right"><button onClick={() => openEdit(r)} className="text-cyan-700 hover:text-cyan-900" data-testid={`btn-open-record-${r.id}`}>{r.read_only ? <><Lock className="w-3 h-3 inline mr-1" /> View</> : <><Pencil className="w-3 h-3 inline mr-1" /> Open</>}</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Matrix */}
        {tab === "matrix" && matrixData && (
          <div className="bg-white border border-slate-200 rounded-md overflow-x-auto" data-testid="matrix-panel">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-600">
                <tr><th className="px-3 py-2 text-left">Staff</th><th className="text-left">Role</th><th className="text-center">Q1</th><th className="text-center">Q2</th><th className="text-center">Q3</th><th className="text-center">Q4</th><th className="text-center">Annual</th><th className="text-left">Next Due</th></tr>
              </thead>
              <tbody>
                {matrixData.rows.map((r) => (
                  <tr key={r.staff_user_id} className="border-t border-slate-100" data-testid={`matrix-row-${r.staff_user_id}`}>
                    <td className="px-3 py-2 font-medium">{r.staff_name}</td>
                    <td className="text-slate-600 text-[11px]">{r.role}</td>
                    {["q1", "q2", "q3", "q4", "annual"].map((k) => (
                      <td key={k} className="text-center">{r[k] ? <CheckCircle2 className="w-4 h-4 text-emerald-600 mx-auto" title={r[k].held_date} /> : <span className="text-slate-300">—</span>}</td>
                    ))}
                    <td className={`whitespace-nowrap`}>
                      <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold border px-1.5 py-0.5 rounded ${DUE_TONE[r.due_state]}`} data-testid={`matrix-due-${r.staff_user_id}`}>
                        {r.next_due_date || "no history"} · {r.due_state.replace("_", " ")}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Actions */}
        {tab === "actions" && (
          <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="actions-panel">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-600">
                <tr><th className="px-3 py-2 text-left">Action</th><th className="text-left">Owner</th><th className="text-left">Priority</th><th className="text-left">Due</th><th className="text-left">From</th><th className="text-left">Status</th><th></th></tr>
              </thead>
              <tbody>
                {actions.length === 0 && <tr><td colSpan={7} className="text-center text-slate-400 py-8 italic">No open actions. 🎉</td></tr>}
                {actions.map((a) => (
                  <tr key={a.id} className="border-t border-slate-100" data-testid={`action-tbl-${a.id}`}>
                    <td className="px-3 py-2">{a.action}</td>
                    <td className="text-slate-600">{a.responsible_person_name || <span className="text-slate-400">Unassigned</span>}</td>
                    <td><span className={`text-[10px] uppercase tracking-widest font-semibold px-1.5 py-0.5 rounded border ${a.priority === "high" ? "bg-red-50 border-red-300 text-red-800" : a.priority === "low" ? "bg-slate-50 border-slate-300 text-slate-600" : "bg-amber-50 border-amber-300 text-amber-800"}`}>{a.priority}</span></td>
                    <td className={`${a.due_date && a.due_date < new Date().toISOString().slice(0,10) ? "text-red-700 font-semibold" : "text-slate-700"}`}>{a.due_date || <span className="text-slate-400">—</span>}</td>
                    <td className="text-slate-600 text-[11px]">{a.staff_name || "—"}<div className="text-[10px] text-slate-400">{a.held_date || ""}</div></td>
                    <td>
                      {canManage ? (
                        <Select value={a.status || "open"} onValueChange={(v) => markAction(a.supervision_id, a.id, { status: v })}>
                          <SelectTrigger className="h-7 text-xs" data-testid={`action-status-${a.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="open">Open</SelectItem><SelectItem value="in_progress">In progress</SelectItem><SelectItem value="completed">Completed</SelectItem><SelectItem value="no_longer_required">No longer required</SelectItem></SelectContent>
                        </Select>
                      ) : <span className="text-xs">{a.status}</span>}
                    </td>
                    <td className="pr-3 text-right"><button onClick={() => { const r = records.find((x) => x.id === a.supervision_id); if (r) openEdit(r); }} className="text-cyan-700 text-xs hover:underline">Open →</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <SupervisionForm open={dialogOpen} onOpenChange={setDialogOpen} initial={editing} staffOptions={staff} currentUser={user} onSaved={() => load()} />
      </div>{/* /supervisions-desktop-wrap */}
    </div>
  );
}

function KpiCard({ label, value, Icon, tone, testid }) {
  const tones = {
    red: "border-red-200 bg-red-50 text-red-800",
    amber: "border-amber-200 bg-amber-50 text-amber-800",
    sky: "border-sky-200 bg-sky-50 text-sky-800",
    slate: "border-slate-200 bg-white text-slate-700",
  };
  return (
    <div className={`border rounded-md px-3 py-2 ${tones[tone] || tones.slate}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-semibold opacity-80"><Icon className="w-3 h-3" />{label}</div>
      <div className="text-2xl font-semibold tabular-nums mt-0.5">{value}</div>
    </div>
  );
}
